import { useIntl } from 'react-intl';

import type { OrderDetailsDocuments } from '#api-bridge/types/orderDetailsDocuments.ts';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import { isServiceOrder } from '#domain/Order.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import { DocumentGroup } from '../../documents/DocumentGroup.tsx';

type DocumentExpanderListItemProps = {
    documents?: OrderDetailsDocuments;
    order: OrderWithSortableKey;
};

export const OrderDocuments = ({ documents, order }: DocumentExpanderListItemProps) => {
    const loadingDocuments = documents?.loadingDocuments ?? [];
    const unloadingDocuments = documents?.unloadingDocuments ?? [];
    const executionDocuments = documents?.executionDocuments ?? [];
    const other = documents?.other ?? [];
    const intl = useIntl();
    const omitTransportOrderAssociationForOrderDocuments = useFeatureToggle(
        'omitTransportOrderAssociationForOrderDocuments',
        false
    );

    const transportOrderAssociation = omitTransportOrderAssociationForOrderDocuments
        ? []
        : ([{ type: 'transport-order', id: order.id }] as const);

    if (isServiceOrder(order)) {
        return (
            <div className='gap-10 display-flex flex-column padding-bottom-15'>
                <DocumentGroup
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.order.documents.plural',
                        defaultMessage: 'Documents',
                    })}
                    documents={executionDocuments}
                    upload={{
                        associations: [
                            { type: 'service-order', id: order.id },
                            { type: 'tour-stop', id: order.executionStop.id },
                        ],
                        target: { stopId: order.executionStop.id },
                        buttonTitle: intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.order.documents.upload.select',
                            defaultMessage: 'Select document',
                        }),
                    }}
                />
            </div>
        );
    }

    return (
        <div className={'gap-10 display-flex flex-column padding-bottom-15'}>
            <DocumentGroup
                label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.order.documents.pickup',
                    defaultMessage: 'Pickup documents',
                })}
                documents={loadingDocuments}
                upload={{
                    associations: [...transportOrderAssociation, { type: 'tour-stop', id: order.loadingStop.id }],
                    target: { stopId: order.loadingStop.id },
                    buttonTitle: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.order.documents.pickup-upload',

                        defaultMessage: 'Upload pickup documents',
                    }),
                }}
            />
            <DocumentGroup
                label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.order.documents.delivery',
                    defaultMessage: 'Delivery documents',
                })}
                documents={unloadingDocuments}
                upload={{
                    associations: [...transportOrderAssociation, { type: 'tour-stop', id: order.unloadingStop.id }],
                    target: { stopId: order.unloadingStop.id },
                    buttonTitle: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.order.documents.delivery-upload',

                        defaultMessage: 'Upload delivery documents',
                    }),
                }}
            />
            {other.length ? (
                <DocumentGroup
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.order.documents.other',
                        defaultMessage: 'Other',
                    })}
                    documents={other}
                />
            ) : null}
        </div>
    );
};
